import React, { useCallback, useEffect, useState } from 'react'

import { Body2, Box, Heading4, useModal } from '@gousto-internal/citrus-react'
import { useDispatch, useSelector } from 'react-redux'

import { CuratedBoxLayout } from '@features/curated-box-carousel'

import { getAvailableBoxSizes } from 'actions/basket'
import { redirect } from 'actions/redirect'
import routes from 'config/routes'
import {
  CuratedBoxWarningModal,
  curatedBoxWarningModalName,
} from 'routes/Menu/MenuRecipesPage/CuratedBoxWarningModal'
import { useAddCuratedBoxRecipes } from 'routes/Menu/MenuRecipesPage/CuratedBoxWarningModal/useAddCuratedBoxRecipes'
import { trackCuratedBoxLoseRecipesWarning } from 'routes/Menu/MenuRecipesPage/trackCuratedBox'
import { showDetailRecipe } from 'routes/Menu/actions/menuRecipeDetails'
import { useBasket } from 'routes/Menu/domains/basket'
import { useCollections } from 'routes/Menu/domains/collections'
import { useSetBrowserCTAVisibility } from 'routes/Menu/domains/menu'
import { getCuratedBoxes } from 'routes/Menu/selectors/menuService'
import { getBasketRecipesCount, getNumPortions, getAvailableNumPortions } from 'selectors/basket'
import { getRecipes } from 'selectors/root'

import { DetailOverlay } from '../Detail'

export type HandleOnClickCuratedBoxProps = {
  curatedBoxRecipes: string[]
  curatedBoxCollectionId: string
  curatedBoxName: string
}

const curatedBoxHeaderCopy = {
  heading: 'Order a preselected box',
  subtitle: 'Save time with a box packed with delicious dinners chosen by our chefs',
}

export const CuratedBoxes = () => {
  const [shouldShowOverlay, setShouldShowOverlay] = useState(false)
  const [hasSeenWarningModal, setHasSeenWarningModal] = useState(false)
  const [selectedCuratedBox, setSelectedCuratedBox] = useState<null | HandleOnClickCuratedBoxProps>(
    null,
  )

  const dispatch = useDispatch()
  const { openModal } = useModal()
  const { canAddRecipes, menuId, numPortions } = useBasket()
  const { setBrowserCTAVisible } = useSetBrowserCTAVisibility()
  const { changeCollectionByIdCuratedBoxes } = useCollections()
  const { addCuratedBoxRecipes } = useAddCuratedBoxRecipes(hasSeenWarningModal)

  const basketRecipesCount = useSelector(getBasketRecipesCount)
  const availableBoxSize = useSelector(getAvailableNumPortions)
  const availableBoxSizeLength = availableBoxSize?.length

  useEffect(() => {
    if (!availableBoxSizeLength) {
      dispatch(getAvailableBoxSizes())
    }
  }, [dispatch, availableBoxSizeLength])

  const handleOnClickOpenMenu = () => dispatch(redirect(routes.client.menu))
  const handleShowRecipeDetails = useCallback(
    (...args) => dispatch(showDetailRecipe(...args)),
    [dispatch],
  )

  const handleEditCuratedBox = (
    curatedBoxRecipes: string[],
    curatedBoxCollectionId: string,
    curatedBoxName: string,
  ) => {
    if (!canAddRecipes) {
      setBrowserCTAVisible()

      return
    }
    if (!basketRecipesCount) {
      addCuratedBoxRecipes({
        curatedBoxRecipes,
        curatedBoxCollectionId,
        curatedBoxName,
      })
    }

    changeCollectionByIdCuratedBoxes(curatedBoxCollectionId)
  }

  const handleOnClickForCuratedBox = (
    curatedBoxRecipes: string[],
    curatedBoxCollectionId: string,
    curatedBoxName: string,
  ) => {
    if (!canAddRecipes) {
      setBrowserCTAVisible()

      return
    }

    setSelectedCuratedBox({
      curatedBoxRecipes,
      curatedBoxCollectionId,
      curatedBoxName,
    })

    if (basketRecipesCount) {
      setHasSeenWarningModal(true)
      openModal(curatedBoxWarningModalName)
      dispatch(
        trackCuratedBoxLoseRecipesWarning(
          numPortions,
          curatedBoxName,
          curatedBoxRecipes,
          curatedBoxCollectionId,
          menuId,
        ),
      )

      return
    }
    addCuratedBoxRecipes({
      curatedBoxRecipes,
      curatedBoxCollectionId,
      curatedBoxName,
    })
  }

  useEffect(() => {
    setShouldShowOverlay(true)
  }, [])

  return (
    <Box paddingH={2}>
      <CuratedBoxWarningModal
        setSelectedCuratedBox={setSelectedCuratedBox}
        selectedCuratedBox={selectedCuratedBox}
        hasSeenWarningModal={hasSeenWarningModal}
      />
      <>
        <Box paddingH={2} paddingV={3}>
          <Heading4>{curatedBoxHeaderCopy.heading}</Heading4>
          <Body2>{curatedBoxHeaderCopy.subtitle}</Body2>
        </Box>
        <CuratedBoxLayout
          handleShowRecipeDetails={handleShowRecipeDetails}
          handleOnClickForCuratedBox={handleOnClickForCuratedBox}
          handleEditCuratedBox={handleEditCuratedBox}
          curatedBoxesMethods={{
            getRecipeStock: {
              isRecipeOutOfStock: () => false,
            },
            getRecipes,
            getCuratedBoxes,
            getNumPortions,
          }}
          handleOnClickOpenMenu={handleOnClickOpenMenu}
        />
        <DetailOverlay showOverlay={shouldShowOverlay} />
      </>
    </Box>
  )
}
